// Live browser speech-to-text helpers for the /_/voice page.
//
// This module captures microphone audio in the browser, downsamples it to mono
// 16 kHz, and streams it as 16-bit little-endian PCM over a WebSocket to the
// nidus-sync /api/audio/transcription proxy (which relays it to Deepgram). The
// pure transcription reducer + message parser in here are unit-tested; the DOM
// capture code is intentionally thin.
//
// The reducer tracks the transcript as ordered utterance segments: a final
// `results` frame appends to the segment currently open, Deepgram's
// `utterance_end` closes that segment so the next final starts a new one, and
// the following `speech_started` lets the reducer measure how long the pause
// lasted (both frames carry Deepgram's audio timestamps). The open segment
// boundary is what the extraction engine receives; the pause timing and the
// per-word provenance of the extraction result are what the page renders.

import {
	approximateText,
	measurementText,
	productText,
	type ContextSpan,
	type ExtractionResult,
	type UtteranceSpan,
} from "@/lib/extraction";

/** Mono PCM sample rate Deepgram is configured for on the server. */
export const PCM_SAMPLE_RATE = 16000;

// --- Server message contract -------------------------------------------------
//
// The server relays normalized JSON frames over the WebSocket:
//   {"type":"results","transcript":"...","is_final":true|false}
//   {"type":"error","message":"..."}
//   {"type":"utterance_end","last_word_end":<seconds>}
//   {"type":"speech_started","timestamp":<seconds>}

export interface TranscriptionResult {
	type: "results";
	transcript: string;
	isFinal: boolean;
}

export interface TranscriptionError {
	type: "error";
	message: string;
}

/** Emitted after UtteranceEndMs of silence — a detected pause. */
export interface TranscriptionUtteranceEnd {
	type: "utterance_end";
	/** Deepgram audio timestamp (seconds) of the last word before the pause. */
	lastWordEnd?: number;
}

/** Emitted when speech resumes after a pause. */
export interface TranscriptionSpeechStarted {
	type: "speech_started";
	/** Deepgram audio timestamp (seconds) where speech resumed. */
	timestamp?: number;
}

export type ServerTranscriptionMessage =
	| TranscriptionResult
	| TranscriptionError
	| TranscriptionUtteranceEnd
	| TranscriptionSpeechStarted;

/** One committed utterance segment (a Deepgram utterance_end closed it). */
export interface UtteranceSegment {
	text: string;
	/** True when an utterance_end closed this segment (a detected pause follows it). */
	pauseAfter: boolean;
	/** Silence in seconds measured after this segment; null when not measured. */
	pauseSeconds: number | null;
}

export interface TranscriptionState {
	/** Committed utterance segments, in speaking order. */
	segments: UtteranceSegment[];
	/** Text accumulated into the utterance still open (before the next utterance_end). */
	current: string;
	/** Current non-final (interim) hypothesis shown while speaking. */
	interim: string;
	/** Non-null when the server reported a transcription error. */
	error: string | null;
	/** last_word_end of the pause awaiting measurement, in audio seconds. */
	pendingPauseEnd: number | null;
}

export const emptyTranscriptionState = (): TranscriptionState => ({
	segments: [],
	current: "",
	interim: "",
	error: null,
	pendingPauseEnd: null,
});

/** Append a final segment to the open utterance with a single separating space. */
function appendSegment(current: string, segment: string): string {
	if (!segment) {
		return current;
	}
	return current ? `${current} ${segment}` : segment;
}

/**
 * Pure state transition for one server message. Final results accrue into the
 * open utterance segment; an utterance_end commits it to `segments` so the next
 * final starts a fresh segment and remembers the pause edge; the following
 * speech_started measures that pause from the two Deepgram audio timestamps.
 * Interim results replace the previous interim hypothesis.
 */
export function reduceTranscription(
	state: TranscriptionState,
	message: ServerTranscriptionMessage,
): TranscriptionState {
	if (message.type === "speech_started") {
		// Speech resumed: the gap between speech_started.timestamp and the
		// preceding utterance_end.last_word_end is the silence Deepgram
		// detected. Hang it on the segment that pause follows.
		if (state.pendingPauseEnd === null || message.timestamp === undefined) {
			return state;
		}
		const pauseSeconds = message.timestamp - state.pendingPauseEnd;
		const last = state.segments.length - 1;
		return {
			...state,
			pendingPauseEnd: null,
			segments:
				pauseSeconds > 0 && last >= 0
					? state.segments.map((segment, index) =>
							index === last ? { ...segment, pauseSeconds } : segment,
						)
					: state.segments,
		};
	}
	if (message.type === "utterance_end") {
		// Close the open utterance so the next final starts a new segment; the
		// pause this event detected follows the segment just committed.
		if (!state.current) {
			return state;
		}
		return {
			...state,
			segments: [
				...state.segments,
				{ text: state.current, pauseAfter: true, pauseSeconds: null },
			],
			current: "",
			pendingPauseEnd: message.lastWordEnd ?? null,
		};
	}
	if (message.type === "error") {
		return { ...state, interim: "", error: message.message };
	}
	if (message.isFinal) {
		return {
			...state,
			current: appendSegment(state.current, message.transcript),
			interim: "",
		};
	}
	return { ...state, interim: message.transcript };
}

/**
 * The utterance segments handed to the extraction engine, in speaking order:
 * the committed segments plus the open one, with empties dropped.
 */
export function utteranceSegments(state: TranscriptionState): string[] {
	return [
		...state.segments.map((segment) => segment.text),
		state.current,
	].filter((segment) => segment.length > 0);
}

/**
 * The transcript as plain text, one utterance per line. That is exactly the
 * segmentation the extraction engine receives, so copying it and pasting it
 * back into the manual-transcript box reproduces the same rendering.
 */
export function rawTranscript(state: TranscriptionState): string {
	return utteranceSegments(state).join("\n");
}

/**
 * Build a transcript state from manually entered text: each non-empty line is
 * one committed utterance segment. Pauses are a Deepgram measurement, so
 * segments entered this way carry none and render without pause markers — the
 * lines themselves are what control segmentation.
 */
export function transcriptFromLines(text: string): TranscriptionState {
	const segments: UtteranceSegment[] = text
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line.length > 0)
		.map((line) => ({ text: line, pauseAfter: false, pauseSeconds: null }));
	return { ...emptyTranscriptionState(), segments };
}

/**
 * Parse a raw WebSocket text frame into a typed server message. Returns null
 * for frames the reducer does not model (e.g. unknown types).
 */
export function parseServerMessage(
	raw: string,
): ServerTranscriptionMessage | null {
	let obj: Record<string, unknown>;
	try {
		obj = JSON.parse(raw) as Record<string, unknown>;
	} catch {
		return null;
	}
	switch (obj.type) {
		case "results":
			return {
				type: "results",
				transcript: typeof obj.transcript === "string" ? obj.transcript : "",
				isFinal: Boolean(obj.is_final),
			};
		case "error":
			return {
				type: "error",
				message:
					typeof obj.message === "string" ? obj.message : "Transcription error",
			};
		case "utterance_end": {
			const message: TranscriptionUtteranceEnd = { type: "utterance_end" };
			if (typeof obj.last_word_end === "number") {
				message.lastWordEnd = obj.last_word_end;
			}
			return message;
		}
		case "speech_started": {
			const message: TranscriptionSpeechStarted = { type: "speech_started" };
			if (typeof obj.timestamp === "number") {
				message.timestamp = obj.timestamp;
			}
			return message;
		}
		default:
			return null;
	}
}

// --- Transcript lines --------------------------------------------------------
//
// The page renders one uneditable line per utterance segment. Each line is
// colored by the extraction context its words fall in, and the words that
// produced an extraction are boxed with the extraction shown on hover. Both
// mappings come from the server: `contexts` and `utteranceSpans` share the
// engine's GLOBAL word-index space, and every extraction layer anchors onto
// that same space — the context-scoped booleans/enumerations/numbers/free
// text/negations, the meanings, and each record's trigger highlights.

/**
 * One renderable piece of a transcript line. A piece is either plain filler
 * (whitespace, punctuation the tokenizer split out, or text spoken after the
 * last extraction request) or a run of one or more tokens that the same
 * extractions cover — a run renders as a single outline around the whole
 * phrase rather than one box per word.
 */
export interface TranscriptPiece {
	/** Raw surface slice of the line's text (original case and punctuation). */
	text: string;
	/** Global engine word indices this piece covers; empty for filler text. */
	wordIndexes: number[];
	/** Extractions covering this piece; empty for plain text. */
	boxes: TranscriptBox[];
	/**
	 * Extraction key the outline colour is derived from, or null for plain
	 * text. When several extractions cover one run they are all listed in the
	 * tooltip and the colour comes from the first key in sorted order, so the
	 * rendering stays deterministic.
	 */
	colorKey: string | null;
	/** Outline hue in degrees for this piece, or null for plain text. */
	hue: number | null;
}

/**
 * One extraction anchored to a span of words, as the tooltip shows it. Every
 * layer of the response that carries word indices (context-scoped booleans,
 * enumerations, numbers, free text and negations, the meaning layer, and the
 * sentence-scoped extraction records) maps onto this one shape.
 */
export interface TranscriptBox {
	/** Extraction layer or record kind, e.g. "enumeration", "count". */
	kind: string;
	/** What was extracted: the field key, concept path, or record label. */
	label: string;
	/** The captured value or short description, when there is one. */
	summary?: string;
	/** Context the extraction belongs to, for context-scoped layers. */
	context?: string;
	/** Extra detail rows (extraction records carry their fields here). */
	fields?: { label: string; value: string }[];
	/** The extraction's key name, e.g. "habitat"; the outline colour keys off it. */
	colorKey: string;
}

/** One utterance line as the page renders it. */
export interface TranscriptLine {
	/** The utterance text (trimmed). */
	text: string;
	/** Context owning the most words of this line, or null when none does. */
	context: string | null;
	/** True when a detected Deepgram pause precedes this line. */
	pauseBefore: boolean;
	/** Silence in seconds before this line; null when it was not measured. */
	pauseSeconds: number | null;
	/** Renderable pieces; concatenating pieces[i].text reproduces `text`. */
	pieces: TranscriptPiece[];
}

/** Format the marker between two lines: measured silence, or a bare pause. */
export function formatPauseDuration(seconds: number | null): string {
	return seconds === null ? "pause" : `${seconds.toFixed(1)} s pause`;
}

/**
 * Build the page's utterance lines from the live reducer state and the last
 * extraction result. Lines are exactly the segments sent to the extraction
 * endpoint (committed segments in order, then the open one, empties dropped),
 * so line i pairs with result.utteranceSpans[i]; lines past the span list —
 * utterances spoken after the last extraction request — render as plain text.
 * The last line's text may have grown past its span, which is safe because the
 * engine only appends: the reported token offsets stay valid.
 */
export function buildTranscriptLines(
	state: TranscriptionState,
	result: ExtractionResult | null,
): TranscriptLine[] {
	const utterances: UtteranceSegment[] = [
		...state.segments,
		{ text: state.current, pauseAfter: false, pauseSeconds: null },
	]
		.map((segment) => ({ ...segment, text: segment.text.trim() }))
		.filter((segment) => segment.text.length > 0);

	const spans = result?.utteranceSpans ?? [];
	const boxes = transcriptBoxes(result);
	const hues = boxHues(boxKeys(boxes));

	return utterances.map((segment, index) => {
		const span = spans[index];
		const previous = index > 0 ? utterances[index - 1] : undefined;
		return {
			text: segment.text,
			context: span ? contextForSpan(result?.contexts ?? [], span) : null,
			pauseBefore: previous?.pauseAfter ?? false,
			pauseSeconds: previous?.pauseSeconds ?? null,
			pieces: span
				? linePieces(segment.text, span, boxes, hues)
				: [plainPiece(segment.text)],
		};
	});
}

/** Every extraction key covered anywhere in the result's box map. */
function boxKeys(boxes: Map<number, TranscriptBox[]>): string[] {
	const keys: string[] = [];
	for (const list of boxes.values()) {
		for (const box of list) {
			keys.push(box.colorKey);
		}
	}
	return keys;
}

/** A plain, unboxed piece covering the whole line (span-less utterances). */
function plainPiece(text: string): TranscriptPiece {
	return { text, wordIndexes: [], boxes: [], colorKey: null, hue: null };
}

/** A stable identity for one extraction box, used for grouping and dedupe. */
function boxSignature(box: TranscriptBox): string {
	return [box.kind, box.label, box.summary ?? "", box.context ?? ""].join(
		"\u0000",
	);
}

/** True when two runs are covered by exactly the same set of extractions. */
function sameBoxList(a: TranscriptBox[], b: TranscriptBox[]): boolean {
	if (a.length !== b.length) {
		return false;
	}
	const left = a.map(boxSignature).sort();
	const right = b.map(boxSignature).sort();
	return left.every((signature, index) => signature === right[index]);
}

/** The key whose hue colours a run: the first in sorted order, so it is stable. */
function primaryColorKey(boxes: TranscriptBox[]): string {
	return [...boxes].sort((a, b) =>
		a.colorKey < b.colorKey ? -1 : a.colorKey > b.colorKey ? 1 : 0,
	)[0].colorKey;
}

/** True when two boxes would render as the same tooltip row. */
function sameBox(a: TranscriptBox, b: TranscriptBox): boolean {
	return sameBoxList([a], [b]);
}

/**
 * Map every global word index covered by an extraction to that extraction.
 *
 * Every span-bearing layer of the response contributes: the context-scoped
 * booleans, enumerations, numbers, free text and negations; the meaning layer;
 * the spoken size measurements and site address; and each extraction record's
 * trigger highlights (anchored by its context window's global start). The
 * knowledge view and session tree are deliberately not consulted — they are
 * projections of these same facts and already have their own panels, so
 * including them would only duplicate tooltip rows. Two layers carry more than
 * a display value and render it through their own helper: a measurement through
 * `measurementText`, and the treatment `product` enumeration through
 * `productText`, so the tooltip shows the product's full name and generic term
 * rather than only the stated form. A hedged size or number is marked by
 * `approximateText`, so an estimate reads in the tooltip exactly as it does in
 * the extraction table and the session-tree cells.
 */
function transcriptBoxes(
	result: ExtractionResult | null,
): Map<number, TranscriptBox[]> {
	const boxes = new Map<number, TranscriptBox[]>();
	if (!result) {
		return boxes;
	}

	const add = (start: number, end: number, box: TranscriptBox) => {
		if (start < 0 || end < start) {
			return;
		}
		for (let word = start; word <= end; word++) {
			const list = boxes.get(word);
			if (!list) {
				boxes.set(word, [box]);
			} else if (!list.some((existing) => sameBox(existing, box))) {
				list.push(box);
			}
		}
	};

	for (const item of result.contextBooleans) {
		add(item.start, item.end, {
			kind: "boolean",
			label: item.key,
			summary: item.value ? "true" : "false",
			context: item.context,
			colorKey: item.key,
		});
	}
	for (const item of result.contextEnumerations) {
		add(item.start, item.end, {
			kind: "enumeration",
			label: item.key,
			// A treatment product's enumeration carries its identity behind the
			// stated value, so the tooltip shows all three facets.
			summary: item.product ? productText(item.product) : item.value,
			context: item.context,
			colorKey: item.key,
		});
	}
	for (const item of result.contextNumbers) {
		add(item.start, item.end, {
			kind: "number",
			label: item.field,
			summary: approximateText(
				[String(item.count), item.unit]
					.filter((part) => part.length > 0)
					.join(" "),
				item.approximate,
			),
			context: item.context,
			colorKey: item.field,
		});
	}
	for (const item of result.contextFreeText) {
		add(item.start, item.end, {
			kind: "free text",
			label: item.key,
			summary: item.text,
			context: item.context,
			colorKey: item.key,
		});
	}
	for (const item of result.contextNegations) {
		add(item.start, item.end, {
			kind: "negation",
			label: item.context,
			summary: "no findings",
			colorKey: item.context,
		});
	}
	for (const meaning of result.meanings) {
		add(meaning.start, meaning.end, {
			kind: "meaning",
			label: meaning.attribute
				? `${meaning.concept}.${meaning.attribute}`
				: meaning.concept,
			summary: meaning.value,
			colorKey: meaning.attribute ?? meaning.concept,
		});
	}
	for (const record of result.extractions) {
		const context = record.context;
		if (!context || context.start < 0) {
			continue;
		}
		const box: TranscriptBox = {
			kind: record.kind,
			label: record.label || record.kind,
			summary: record.summary,
			colorKey: record.kind,
			fields: record.fields.map((field) => ({
				label: field.label || field.key,
				value: approximateText(
					field.unit ? `${field.value} ${field.unit}` : field.value,
					field.approximate,
				),
			})),
		};
		for (const range of context.highlights) {
			add(context.start + range.start, context.start + range.end, box);
		}
	}
	for (const measurement of result.measurements ?? []) {
		add(measurement.start, measurement.end, {
			kind: "measurement",
			label: measurement.label,
			summary: approximateText(
				measurementText(measurement),
				measurement.approximate,
			),
			colorKey: measurement.key,
		});
	}
	if (result.address) {
		add(result.address.start, result.address.end, {
			kind: "address",
			label: "address",
			summary: result.address.text,
			colorKey: "address",
		});
	}
	return boxes;
}

/** The context owning the most words of an utterance span, or null. */
function contextForSpan(
	contexts: ContextSpan[],
	span: UtteranceSpan,
): string | null {
	let best: string | null = null;
	let bestOverlap = 0;
	for (const context of contexts) {
		const overlap =
			Math.min(context.end, span.end) - Math.max(context.start, span.start) + 1;
		if (overlap > bestOverlap) {
			bestOverlap = overlap;
			best = context.name;
		}
	}
	return best;
}

/** One token or filler slice of a line, before grouping into pieces. */
interface LineAtom {
	text: string;
	wordIndex: number | null;
	boxes: TranscriptBox[];
}

/** Step used to walk a colliding hue clear of the ones already taken. */
const GOLDEN_ANGLE = 137.508;

/** Raw deterministic hue for a key: FNV-1a mapped onto the colour wheel. */
function hashHue(key: string): number {
	let hash = 2166136261;
	for (let index = 0; index < key.length; index++) {
		hash ^= key.charCodeAt(index);
		hash = Math.imul(hash, 16777619);
	}
	return (hash >>> 0) % 360;
}

/** Shortest distance between two hues on the wheel, in degrees. */
function hueDistance(a: number, b: number): number {
	const raw = Math.abs(a - b) % 360;
	return Math.min(raw, 360 - raw);
}

/**
 * Assign every extraction key in one transcript a distinct outline hue.
 *
 * The base hue is a stable hash of the key, so a key ("habitat") keeps the same
 * colour from one transcript to the next. A hash alone cannot guarantee that
 * two keys land far apart on the wheel — "landUse" and "water" differ by 7°,
 * which reads as the same colour — so keys are assigned in sorted order and any
 * key landing within the separation band of one already taken is rotated by the
 * golden angle until it clears. The result is deterministic for a given set of
 * keys and never paints two extractions in one transcript the same colour.
 */
export function boxHues(keys: Iterable<string>): Map<string, number> {
	const unique = [...new Set(keys)].sort();
	const separation = Math.min(
		60,
		Math.max(12, 360 / Math.max(unique.length, 1)),
	);
	const taken: number[] = [];
	const hues = new Map<string, number>();
	for (const key of unique) {
		let hue = hashHue(key);
		for (let attempt = 0; attempt < 64; attempt++) {
			if (!taken.some((other) => hueDistance(other, hue) < separation)) {
				break;
			}
			hue = (hue + GOLDEN_ANGLE) % 360;
		}
		taken.push(hue);
		hues.set(key, Math.round(hue * 10) / 10);
	}
	return hues;
}

/**
 * Split a line's text into renderable pieces using the server's token offsets.
 *
 * Atoms are the token and filler slices; consecutive tokens covered by the same
 * extractions are then merged into one piece, so an extraction spanning several
 * words renders as a single outline around the whole phrase instead of per-word
 * highlights. Filler between those words is absorbed into the outline, while
 * filler before and after is given back to the line. Offsets are code-point
 * indices, so the text is split with Array.from.
 */
function linePieces(
	text: string,
	span: UtteranceSpan,
	boxes: Map<number, TranscriptBox[]>,
	hues: Map<string, number>,
): TranscriptPiece[] {
	const atoms = lineAtoms(text, span, boxes);
	const pieces: TranscriptPiece[] = [];
	let index = 0;
	while (index < atoms.length) {
		const atom = atoms[index];
		if (atom.wordIndex === null || atom.boxes.length === 0) {
			pieces.push({
				text: atom.text,
				wordIndexes: atom.wordIndex === null ? [] : [atom.wordIndex],
				boxes: [],
				colorKey: null,
				hue: null,
			});
			index++;
			continue;
		}
		// Extend the run while the covering extractions stay the same. Filler
		// between the tokens is absorbed so the outline encloses the phrase.
		let end = index;
		const wordIndexes: number[] = [];
		while (end < atoms.length) {
			const next = atoms[end];
			if (next.wordIndex === null) {
				end++;
				continue;
			}
			if (!sameBoxList(next.boxes, atom.boxes)) {
				break;
			}
			wordIndexes.push(next.wordIndex);
			end++;
		}
		// Hand back filler that turned out to trail the run.
		while (end > index && atoms[end - 1].wordIndex === null) {
			end--;
		}
		const colorKey = primaryColorKey(atom.boxes);
		pieces.push({
			text: atoms
				.slice(index, end)
				.map((absorbed) => absorbed.text)
				.join(""),
			wordIndexes,
			boxes: atom.boxes,
			colorKey,
			hue: hues.get(colorKey) ?? null,
		});
		index = end;
	}
	return pieces;
}

/** Slice a line into ordered token and filler atoms using the server offsets. */
function lineAtoms(
	text: string,
	span: UtteranceSpan,
	boxes: Map<number, TranscriptBox[]>,
): LineAtom[] {
	if (span.start < 0 || span.words.length === 0) {
		return [{ text, wordIndex: null, boxes: [] }];
	}
	const chars = Array.from(text);
	const atoms: LineAtom[] = [];
	let cursor = 0;
	for (const [index, word] of span.words.entries()) {
		// Unlocated (-1) tokens and any overlap are left inside a filler gap;
		// offsets only ever move forward.
		if (word.start < cursor || word.end < word.start) {
			continue;
		}
		if (word.start >= chars.length) {
			break;
		}
		const end = Math.min(word.end, chars.length - 1);
		if (word.start > cursor) {
			atoms.push({
				text: chars.slice(cursor, word.start).join(""),
				wordIndex: null,
				boxes: [],
			});
		}
		const wordIndex = span.start + index;
		atoms.push({
			text: chars.slice(word.start, end + 1).join(""),
			wordIndex,
			boxes: boxes.get(wordIndex) ?? [],
		});
		cursor = end + 1;
	}
	if (cursor < chars.length) {
		atoms.push({
			text: chars.slice(cursor).join(""),
			wordIndex: null,
			boxes: [],
		});
	}
	return atoms;
}

// --- Microphone capture ------------------------------------------------------

export interface MicCapture {
	/** Stop capture, disconnect the audio graph, and release the mic. */
	stop(): void;
}

export interface MicCaptureCallbacks {
	/** Called with each chunk of 16-bit PCM bytes at PCM_SAMPLE_RATE. */
	onAudio(pcmBytes: ArrayBuffer): void;
	/**
	 * Called for each captured chunk with the normalized loudness (0..1) and
	 * whether the chunk clipped, so the UI can show an input-level meter.
	 */
	onLevel?(level: number, clipping: boolean): void;
}

/** dBFS treated as the bottom of the level meter. */
const LEVEL_FLOOR_DB = -60;

/** Sample magnitude treated as clipping. */
const CLIPPING_THRESHOLD = 0.99;

export interface InputLevel {
	/** Normalized loudness 0..1 (RMS mapped from LEVEL_FLOOR_DB to 0 dBFS). */
	level: number;
	/** True when any sample reached CLIPPING_THRESHOLD. */
	clipping: boolean;
}

/**
 * Measure one captured chunk of float samples in [-1, 1] for the input-level
 * meter: RMS loudness normalized over LEVEL_FLOOR_DB..0 dBFS, plus whether the
 * chunk clipped.
 */
export function measureInputLevel(samples: Float32Array): InputLevel {
	let sumSquares = 0;
	let clipping = false;
	for (let i = 0; i < samples.length; i++) {
		const sample = samples[i];
		sumSquares += sample * sample;
		if (sample >= CLIPPING_THRESHOLD || sample <= -CLIPPING_THRESHOLD) {
			clipping = true;
		}
	}
	const rms = samples.length > 0 ? Math.sqrt(sumSquares / samples.length) : 0;
	const db = rms > 0 ? 20 * Math.log10(rms) : LEVEL_FLOOR_DB;
	const level = Math.min(
		1,
		Math.max(0, (db - LEVEL_FLOOR_DB) / -LEVEL_FLOOR_DB),
	);
	return { level, clipping };
}

/**
 * Request the microphone and return a session that emits downsampled mono
 * 16-bit PCM chunks via onAudio. Resolves only after the user has granted mic
 * access, so a rejection surfaces the browser's permission error to the UI.
 */
export async function startMicCapture(
	callbacks: MicCaptureCallbacks,
): Promise<MicCapture> {
	if (!navigator.mediaDevices?.getUserMedia) {
		throw new Error("Microphone capture is not supported in this browser");
	}
	const AudioContextClass =
		window.AudioContext ??
		(window as unknown as { webkitAudioContext?: typeof AudioContext })
			.webkitAudioContext;
	if (!AudioContextClass) {
		throw new Error("Web Audio is not supported in this browser");
	}

	const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
	const audioContext = new AudioContextClass();
	if (audioContext.state === "suspended") {
		await audioContext.resume();
	}

	const source = audioContext.createMediaStreamSource(stream);
	const processor = audioContext.createScriptProcessor(4096, 1, 1);
	const downsample = createPcmDownsampler(
		audioContext.sampleRate,
		PCM_SAMPLE_RATE,
	);

	source.connect(processor);
	// Connecting the processor to the destination keeps the audio graph alive.
	processor.connect(audioContext.destination);

	processor.onaudioprocess = (event) => {
		const input = event.inputBuffer.getChannelData(0);
		if (callbacks.onLevel) {
			const { level, clipping } = measureInputLevel(input);
			callbacks.onLevel(level, clipping);
		}
		const samples = downsample(input);
		if (samples.length > 0) {
			callbacks.onAudio(toPcm16LittleEndian(samples));
		}
	};

	let stopped = false;
	return {
		stop() {
			if (stopped) {
				return;
			}
			stopped = true;
			processor.onaudioprocess = null;
			processor.disconnect();
			source.disconnect();
			stream.getTracks().forEach((track) => track.stop());
			void audioContext.close();
		},
	};
}

/**
 * Build a decimating resampler from inputRate to outputRate that carries the
 * fractional remainder across audio chunks (ScriptProcessor delivers 4096
 * frames per event, and boundaries rarely align with the resample ratio).
 */
function createPcmDownsampler(
	inputRate: number,
	outputRate: number,
): (input: Float32Array) => number[] {
	const ratio = inputRate / outputRate;
	let accumulator = 0;
	return (input: Float32Array): number[] => {
		const output: number[] = [];
		for (let i = 0; i < input.length; i++) {
			accumulator += 1;
			while (accumulator >= ratio) {
				output.push(input[i]);
				accumulator -= ratio;
			}
		}
		return output;
	};
}

/** Pack float samples in [-1, 1] as signed 16-bit little-endian PCM bytes. */
function toPcm16LittleEndian(samples: number[]): ArrayBuffer {
	const buffer = new ArrayBuffer(samples.length * 2);
	const view = new DataView(buffer);
	for (let i = 0; i < samples.length; i++) {
		const clamped = Math.max(-1, Math.min(1, samples[i]));
		view.setInt16(
			i * 2,
			clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff,
			true,
		);
	}
	return buffer;
}
